/**
 * The codes for one home, on the household's page. Office work.
 *
 * Labels are listed and values are never in a list: GET /api/secrets returns which codes exist and
 * what each opens, and a value is a separate audited reveal that hides itself again after a minute
 * (`RevealCode`). An owner with a stale session is asked to sign in again before one is shown.
 *
 * THE LIST IS DRAWN, NOT HIDDEN BEHIND A DISCLOSURE. Listing labels is a cheap read with no audit
 * row of its own (`scopedSecretLabels` on the server), and the question this card answers standing
 * at a door is "is there a code for this house at all", which a closed panel cannot answer. The
 * value still costs a press, and the press is still on the record.
 *
 * The three things that CHANGE a code sit behind a per-code button rather than beside every row:
 * rotating one, marking that somebody who saw it has left, and taking it off the record are done
 * once in a while at a desk, and they are not what this card is for.
 *
 * An alarm code is not kept at all (the owner, 2026-10-24, business #47): the alarm is the
 * household's, and the business never holds its code. The form does not offer the kind, the server
 * refuses it, and a row kept from before is marked retired with the server's own sentence, offering
 * only its removal on the record. `docs/identity.md` has the reasoning and the offboarding runbook
 * that the "somebody who saw this has left" button turns into a column.
 *
 * The one coral thing here is a code that has to change because somebody who saw it has left. That
 * is a gap somebody has to close. "Owner only" is a fact about who may look, not a gap, so it is a
 * quiet pill.
 */
import { useEffect, useState, type FormEvent } from 'react';
import {
  SENSITIVITIES,
  WRITABLE_ACCESS_SECRET_KINDS,
  type Sensitivity,
  type WritableAccessSecretKind,
} from '@fk/shared';
import {
  ApiError,
  createSecret,
  listSecrets,
  markRotationDue,
  removeSecret,
  rotateSecret,
  type SecretRow,
} from '../api.ts';
import { formatInstant } from '../format.ts';
import { ACCESS_SECRET_KIND_LABELS, SENSITIVITY_LABELS } from '../labels.ts';
import { Pill } from './AppShell.tsx';
import { Button, SelectField, TextField } from './Field.tsx';
import { ErrorNotice, SavedNotice } from './Notice.tsx';
import { RevealCode } from './RevealCode.tsx';
import { GapPill } from './StatusPills.tsx';

/** What a row is called: the lock it opens, and what kind of code it is. */
function nameOf(row: SecretRow): string {
  const kind = ACCESS_SECRET_KIND_LABELS[row.kind];
  return row.label ? `${row.label}, ${kind.toLowerCase()}` : kind;
}

export function HomeCodes({ siteId, returnPath }: { siteId: string; returnPath: string }) {
  const [rows, setRows] = useState<readonly SecretRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);

  const reload = (sentence: string | null) => {
    setSaid(sentence);
    setAttempt((n) => n + 1);
  };

  useEffect(() => {
    let live = true;
    setError(null);
    listSecrets(siteId)
      .then((answer) => {
        if (live) setRows(answer.secrets);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The codes did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [siteId, attempt]);

  return (
    <div className="flex flex-col gap-3">
      {said ? <SavedNotice>{said}</SavedNotice> : null}

      {error ? (
        <ErrorNotice
          title="The codes did not load"
          message={error.message}
          onRetry={() => reload(null)}
        />
      ) : rows === null ? (
        <p className="text-sm text-navy/60" role="status">
          Loading the codes.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-navy/70">
          No codes on file for this home. Whoever is on the next visit has to be let in another way.
        </p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li key={row.id} className="border-b border-navy/8 py-2 first:pt-0 last:border-b-0">
              <CodeRow row={row} returnPath={returnPath} onChanged={reload} />
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs leading-4 text-navy/65">
        A code shows for one minute, and your name goes on the record beside the moment you looked.
        A cleaner sees one only for a home they are on, from twelve hours before the visit to four
        hours after. No alarm code is kept: the alarm is the household's.
      </p>

      <AddCode siteId={siteId} onSaved={() => reload('Sealed and saved.')} />
    </div>
  );
}

function CodeRow({
  row,
  returnPath,
  onChanged,
}: {
  row: SecretRow;
  returnPath: string;
  onChanged: (sentence: string) => void;
}) {
  const [managing, setManaging] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** True when the work landed. A refusal shows the server's sentence and answers false. */
  async function run(work: () => Promise<unknown>, sentence: string): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onChanged(sentence);
      return true;
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 grow text-sm leading-5 font-medium">{nameOf(row)}</p>
        {row.retired ? <Pill>Retired</Pill> : null}
        {row.sensitivity === 'owner_only' ? <Pill>{SENSITIVITY_LABELS.owner_only}</Pill> : null}
        {row.rotationDueAt && !row.retired ? <GapPill>Change this code</GapPill> : null}
      </div>

      {row.retired ? (
        <p className="mt-1 text-xs leading-4 text-navy/65">
          {row.retired} Remove it from the record.
        </p>
      ) : null}

      {row.rotationDueAt && !row.retired ? (
        <p className="mt-1 text-xs leading-4 text-navy/65">
          Somebody who saw this has left, since {formatInstant(row.rotationDueAt)}. Ask the client
          for a new one and record it here.
        </p>
      ) : null}

      {problem ? (
        <div className="mt-2">
          <ErrorNotice title="Not saved" message={problem} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 grow">
          <RevealCode secretId={row.id} returnPath={returnPath} />
        </div>
        <button
          type="button"
          onClick={() => {
            setManaging((was) => !was);
            setRotating(false);
            setConfirmingRemove(false);
          }}
          disabled={busy}
          className="inline-flex min-h-11 desk:min-h-9 shrink-0 items-center text-sm font-medium text-navy/70 underline disabled:opacity-50"
        >
          {managing ? 'Done' : 'Change or remove it'}
        </button>
      </div>

      {managing ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {!row.retired ? (
            <Button tone="quiet" onClick={() => setRotating((was) => !was)} disabled={busy}>
              {rotating ? 'Never mind' : 'Rotate'}
            </Button>
          ) : null}
          {!row.rotationDueAt && !row.retired ? (
            <Button
              tone="quiet"
              disabled={busy}
              onClick={() =>
                run(
                  () => markRotationDue(row.id),
                  'Marked. It stays flagged until the code actually changes.',
                )
              }
            >
              Somebody who saw this has left
            </Button>
          ) : null}
          {!confirmingRemove ? (
            <Button tone="quiet" onClick={() => setConfirmingRemove(true)} disabled={busy}>
              Remove
            </Button>
          ) : null}
        </div>
      ) : null}

      {managing && rotating && !row.retired ? (
        <form
          className="mt-3 rounded-lg border border-navy/15 p-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!newValue.trim()) {
              setProblem('Type the new code first.');
              return;
            }
            // The typed code stays in the box on a refusal, under the server's sentence.
            void run(() => rotateSecret(row.id, newValue.trim()), 'The new code is sealed.').then(
              (sealed) => {
                if (!sealed) return;
                setNewValue('');
                setRotating(false);
              },
            );
          }}
          noValidate
        >
          <TextField
            id={`rotate-${row.id}`}
            label="The new code"
            value={newValue}
            onChange={setNewValue}
            autoComplete="off"
            hint="Replaces the old one under the current key and clears the flag above."
          />
          <Button type="submit" disabled={busy}>
            {busy ? 'Sealing' : 'Save the new code'}
          </Button>
        </form>
      ) : null}

      {managing && confirmingRemove ? (
        <div className="mt-3 rounded-lg border border-coral/40 bg-parchment p-3">
          <p className="text-sm">
            Tap again to remove this code from the record. Anybody who needs it afterwards will have
            to ask the client. The history of who saw it is kept.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              disabled={busy}
              onClick={() => run(() => removeSecret(row.id), 'Removed from the record.')}
            >
              Yes, remove it
            </Button>
            <Button tone="quiet" onClick={() => setConfirmingRemove(false)} disabled={busy}>
              Keep it
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AddCode({ siteId, onSaved }: { siteId: string; onSaved: () => void }) {
  const [kind, setKind] = useState<WritableAccessSecretKind | ''>('');
  const [label, setLabel] = useState('');
  const [sensitivity, setSensitivity] = useState<Sensitivity>('normal');
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (!kind) {
      setProblem('Say what kind of code this is.');
      return;
    }
    if (!label.trim()) {
      setProblem('Say which lock it opens, like "front door lockbox".');
      return;
    }
    if (!value.trim()) {
      setProblem('Type the code itself.');
      return;
    }
    setBusy(true);
    try {
      await createSecret({
        siteId,
        kind,
        label: label.trim(),
        value: value.trim(),
        sensitivity,
      });
      setKind('');
      setLabel('');
      setSensitivity('normal');
      setValue('');
      onSaved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'The code was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="rounded-lg border border-navy/15 p-3">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
        Add a code
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <SelectField
          id={`code-kind-${siteId}`}
          label="What kind"
          required
          value={kind}
          onChange={setKind}
          options={WRITABLE_ACCESS_SECRET_KINDS}
          toLabel={(option) => ACCESS_SECRET_KIND_LABELS[option]}
          placeholder="Choose one"
          hint="No alarm code: the alarm is the household's, and we never hold its code."
        />
        <TextField
          id={`code-label-${siteId}`}
          label="Which lock it opens"
          required
          value={label}
          onChange={setLabel}
          autoComplete="off"
          placeholder="front door lockbox"
        />
        <SelectField
          id={`code-sensitivity-${siteId}`}
          label="Who may see it"
          value={sensitivity}
          onChange={setSensitivity}
          options={SENSITIVITIES}
          toLabel={(option) => SENSITIVITY_LABELS[option]}
        />
        <TextField
          id={`code-value-${siteId}`}
          label="The code"
          required
          value={value}
          onChange={setValue}
          autoComplete="off"
          hint="Sealed before it is stored. Nobody, including this screen, reads it back without a record."
        />
        <Button type="submit" tone="quiet" full disabled={busy}>
          {busy ? 'Sealing' : 'Seal and save'}
        </Button>
      </form>
    </details>
  );
}
